import React, { useEffect, useRef, useState } from "react";
import { stateOptions, termOptions } from "../utils/options";
import { Controller, useForm } from "react-hook-form";
import { FaExclamationCircle } from "react-icons/fa";
import Select from "react-select";
import { yupResolver } from "@hookform/resolvers/yup";
import { validationSchema } from "../utils/validationSchema";
import DataModal from "./DataModal";
import ToolTip from "./ToolTip";
import { IoMdInformationCircleOutline } from "react-icons/io";
import {
  extractSlugPart,
  formatDateString,
  unSlugify,
} from "../utils/helperFunctions";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../Redux/Loader/loadingSlice";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";

const Form = ({
  setExhibitData,
  currentDate,
  fillForm,
  setFileUploads,
  isPartner,
}) => {
  const [formState, setFormState] = useState(null);
  const [errorOpen, setErrorOpen] = useState(false);
  const [overrideState, setOverrideState] = useState({}); // keys like cam, tax, meeting...
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const pendingSubmitRef = useRef(null);
  const askedRef = useRef(new Set());
  // remembers the values at the moment the user clicked "No" on the sqft modal
  const lastSqftNoRef = useRef(null);
  // track last values to only re-prompt sqft/desk after a real change
  const lastSqftRef = useRef(null);
  const lastDesksRef = useRef(null);
  // random, non-addressy name so Chrome won't treat it like an address
  const [ghostName] = useState(
    () => `q_${Math.random().toString(36).slice(2)}`
  );
  const [addrSearch, setAddrSearch] = useState("");
  useEffect(() => {
    // initialize from any existing RHF value
    setAddrSearch(getValues("locationAddress") || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const location = useLocation();
  const loading = useSelector((state) => state.loading);
  // allow past + future, but clamp to a reasonable window
  const MIN_START = `${new Date().getFullYear() - 5}-01-01`; // 01/01/(currentYear+5)
  const MAX_START = `${new Date().getFullYear() + 5}-12-31`; // 12/31/(currentYear+5)

  useEffect(() => {
    console.log(overrideState);
  }, [overrideState]);

  useEffect(() => {
    console.log(loading);
  }, [loading]);

  const handleChange = (e) => {
    for (let i = 0; i < e.target.files.length; i++) {
      const newFile = e.target.files[i];
      newFile["id"] = Math.random();
      setFileUploads((prevState) => [...prevState, newFile]);
    }
  };

  const handleClearAll = () => {
    // Clear all RHF fields to their default (empty)
    reset();

    // Clear the visible address search box
    setAddrSearch("");

    // Reset local form state and overrides
    setFormState(null);
    setOverrideState({});
    setErrorOpen(false);

    // Clear any uploaded files if you want "all fields" to include files
    if (typeof setFileUploads === "function") {
      setFileUploads([]);
    }
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // merge-only enqueuer for the sqft modal (can queue even if another modal is open)
  const enqueueSqftConfirm = () => {
    setErrorOpen((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      if (current.includes("sqftLeased")) return prev;
      // ensure sqft can always be re-asked on future changes
      askedRef.current.delete("sqftLeased");
      return [...current, "sqftLeased"];
    });
  };

  // blur-only check for sqft average (never part of submit checks)
  const checkSqftAverageOnChange = () => {
    const sqft = Number(getValues("sqftLeased"));
    const desks = Number(getValues("desks"));

    const changed =
      sqft !== lastSqftRef.current || desks !== lastDesksRef.current;

    if (!changed) return;

    // since values changed, clear the "No" snapshot — user is editing
    lastSqftNoRef.current = null;

    lastSqftRef.current = sqft;
    lastDesksRef.current = desks;

    // trigger modal if high avg (>= 200)
    if (sqft && desks > 0 && sqft / desks >= 200) {
      clearOverride("sqftLeased");
      askedRef.current.delete("sqftLeased");
      enqueueSqftConfirm();
    }
  };

  const clearOverride = (key) =>
    setOverrideState((prev) => (prev[key] ? { ...prev, [key]: false } : prev));

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty },
    getValues,
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  const [auto, setAuto] = useState(null);
  const locationAddrRef = useRef(null);

  // helper to get a component by type
  const pick = (components, type) => {
    const c = components?.find((x) => x.types?.includes(type));
    return c
      ? { long: c.long_name, short: c.short_name }
      : { long: "", short: "" };
  };

  // put this where your other handlers live in Form.jsx// put this where your other handlers live in Form.jsx

  const handleModalNo = (key) => {
    const facility = getValues("facility") || [];
    const amenity = getValues("amenity") || [];

    switch (key) {
      case "cam":
      case "tax": {
        const next = facility.includes(key) ? facility : [...facility, key];
        setValue("facility", next, { shouldDirty: true, shouldValidate: true });
        break;
      }

      case "meeting":
      case "kitchen":
      case "furnished":
      case "copy":
      case "receptionist": {
        const next = amenity.filter((v) => v !== key);
        setValue("amenity", next, { shouldDirty: true, shouldValidate: true });
        break;
      }

      case "desks":
      case "sqftLeased": {
        // record the exact values when user said "No" so we can enforce on submit if unchanged
        const sqft = Number(getValues("sqftLeased"));
        const desks = Number(getValues("desks"));
        lastSqftNoRef.current = { sqft, desks };
        break;
      }

      default:
        break;
    }

    // force future re-asks until user affirms "Yes" or changes the data
    setOverrideState((prev) => ({ ...prev, [key]: false }));
    askedRef.current.delete(key);
  };

  const handlePlaceChanged = () => {
    if (!auto) return;
    const place = auto.getPlace();
    const comps = place?.address_components || [];

    const streetNo = pick(comps, "street_number").long;
    const route = pick(comps, "route").long;
    const line1 = [streetNo, route].filter(Boolean).join(" ");

    const city =
      pick(comps, "locality").long ||
      pick(comps, "sublocality").long ||
      pick(comps, "administrative_area_level_3").long;

    const stateShort = pick(comps, "administrative_area_level_1").short;
    const zip = pick(comps, "postal_code").long;

    // RHF values (real form fields)
    setValue("locationAddress", line1, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("city", city, { shouldDirty: true, shouldValidate: true });
    setValue("zipcode", zip, { shouldDirty: true, shouldValidate: true });

    const stateOpt =
      stateOptions.find((o) => o.value === stateShort) ||
      (stateShort ? { value: stateShort, label: stateShort } : null);
    if (stateOpt)
      setValue("state", stateOpt, { shouldDirty: true, shouldValidate: true });

    // Make the visible search box show ONLY "123 Main St"
    setAddrSearch(line1);

    // In case Google wrote formatted text, overwrite on next tick
    if (locationAddrRef.current) {
      setTimeout(() => {
        locationAddrRef.current.value = line1;
      }, 0);
    }
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const buildAttestations = (data) => {
    const keys = [];

    // Submit-time confirmations ONLY (no sqft average here)
    if (!data.facility.includes("cam")) keys.push("cam");
    if (!data.facility.includes("tax")) keys.push("tax");

    if (data.sqftLeased >= 500 && data.amenity.includes("meeting"))
      keys.push("meeting");
    if (data.sqftLeased >= 500 && data.amenity.includes("kitchen"))
      keys.push("kitchen");
    if (data.sqftLeased >= 500 && data.amenity.includes("furnished"))
      keys.push("furnished");
    if (data.sqftLeased >= 500 && data.amenity.includes("copy"))
      keys.push("copy");
    if (data.sqftLeased >= 500 && data.amenity.includes("receptionist"))
      keys.push("receptionist");

    if (data.desks >= 2) keys.push("desks");

    return [...new Set(keys)];
  };

  const onSubmit = async (data, status) => {
    const stateValue = data.state && data.state.value ? data.state.value : "";
    const exhibit = {
      serviceProvider: data.serviceProvider ?? "",
      commonName: data.commonName ?? "",
      locationAddress: data.locationAddress ?? "",
      city: data.city ?? "",
      state: stateValue,
      zipcode: data.zipcode ?? "",
      sqftLeased: data.sqftLeased ?? "",
      term: data.term?.label || "",
      desks: data.desks ?? "",
      propertyType: data.propertyType ?? "",
      spaceType: data.spaceType ?? "",
      settingType: data.settingType ?? "",
      facility: data.facility ?? [],
      utility: data.utility ?? [],
      amenity: data.amenity ?? [],
      startDate: data.startDate ? formatDateString(data.startDate) : "",
      description: data.description ?? "",
      createdDate: currentDate,
      status,
    };

    // Skip business-rule confirmations for drafts
    if (status !== "Saved") {
      // Build the standard confirmation keys (NO sqft here)
      const baseKeys = buildAttestations(data);

      // Apply overrides + "already asked" gating for the standard keys
      let toConfirm = baseKeys.filter(
        (k) => !overrideState[k] && !askedRef.current.has(k)
      );

      // sqft-per-desk is a separate trigger:
      // - It fires on blur (you already do this).
      // - It also fires on submission ONLY if the ratio is still high AND the user has NOT affirmed "Yes".
      const sqft = Number(data.sqftLeased);
      const desks = Number(data.desks);
      const ratioHigh = sqft && desks > 0 && sqft / desks >= 200;

      if (ratioHigh && overrideState.sqftLeased !== true) {
        // Force sqftLeased to be (re)confirmed on submit if not affirmed.
        // Put it first so the user sees this message before the others.
        toConfirm = [
          "sqftLeased",
          ...toConfirm.filter((k) => k !== "sqftLeased"),
        ];
      }

      if (toConfirm.length) {
        // Remember what we're trying to submit, and open the modal
        pendingSubmitRef.current = { data, status };
        setFormState((prev) => ({ ...prev, ...exhibit }));
        setErrorOpen(Array.from(new Set(toConfirm))); // uniqueness guard
        toConfirm.forEach((k) => askedRef.current.add(k));
        dispatch(setLoading(false));
        return;
      }
    }

    // No confirmations needed → proceed
    await fillForm(exhibit);
    askedRef.current.clear();
    setErrorOpen(false);
  };

  return (
    <>
      {errorOpen && Array.isArray(errorOpen) && errorOpen.length > 0 && (
        <DataModal
          setErrorOpen={setErrorOpen}
          errorOpen={errorOpen}
          formState={formState}
          setFormState={setFormState}
          setOverrideState={setOverrideState}
          onNo={handleModalNo}
          onComplete={async ({ allYes } = {}) => {
            // Only auto-submit when all modal answers were "Yes"
            if (!pendingSubmitRef.current) return;
            const { data, status } = pendingSubmitRef.current;
            pendingSubmitRef.current = null;

            if (allYes) {
              await onSubmit(data, status);
            }
          }}
        />
      )}

      <div className="exhibit-form-container">
        <div>
          <h1 className="new-exhibit-h1">
            Office Lease and Other Real Estate Services
          </h1>
          <h4>(complete one form per location address)</h4>
        </div>

        <form
          className="new-exhibit-form"
          onSubmit={(e) => e.preventDefault()} // ← prevent double submit
          noValidate
          autoComplete="off"
        >
          {/* Autofill decoys so Chrome fills these instead of your visible box */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              width: 0,
              height: 0,
              overflow: "hidden",
            }}
          >
            <input
              type="text"
              name="street-address"
              autoComplete="street-address"
              tabIndex={-1}
              style={{ opacity: 0, width: 1, height: 1 }}
            />
            <input
              type="text"
              name="address-line2"
              autoComplete="address-line2"
              tabIndex={-1}
              style={{ opacity: 0, width: 1, height: 1 }}
            />
            <input
              type="text"
              name="address-level2"
              autoComplete="address-level2"
              tabIndex={-1}
              style={{ opacity: 0, width: 1, height: 1 }}
            />
            <input
              type="text"
              name="address-level1"
              autoComplete="address-level1"
              tabIndex={-1}
              style={{ opacity: 0, width: 1, height: 1 }}
            />
            <input
              type="text"
              name="postal-code"
              autoComplete="postal-code"
              tabIndex={-1}
              style={{ opacity: 0, width: 1, height: 1 }}
            />
          </div>

          <div
            className={
              errors.serviceProvider
                ? "job-form-input-container error"
                : "job-form-input-container"
            }
          >
            {errors.serviceProvider && (
              <p className="error-message">
                {" "}
                <span style={{ marginRight: "5px" }}>
                  <FaExclamationCircle />
                </span>
                {errors.serviceProvider.message}
              </p>
            )}
            <div className="job-form-input">
              <label htmlFor="serviceProvider">
                Service Provider (Lessor/Landlord)
              </label>
              <input
                id="serviceProvider"
                type="text"
                cols={35}
                placeholder=""
                className="input"
                {...register("serviceProvider")}
              />
            </div>
          </div>
          <div
            className={
              errors.commonName
                ? "job-form-input-container error"
                : "job-form-input-container"
            }
          >
            {errors.commonName && (
              <p className="error-message">
                {" "}
                <span style={{ marginRight: "5px" }}>
                  <FaExclamationCircle />
                </span>
                {errors.commonName.message}
              </p>
            )}
            <div className="job-form-input">
              <label htmlFor="commonName">Common/Branded Name</label>
              <input
                id="commonName"
                type="text"
                cols={35}
                placeholder=""
                className="input"
                {...register("commonName")}
              />
            </div>
          </div>

          <div
            className={
              errors.locationAddress
                ? "job-form-input-container error"
                : "job-form-input-container"
            }
          >
            {errors.locationAddress && (
              <p className="error-message">
                <span style={{ marginRight: "5px" }}>
                  <FaExclamationCircle />
                </span>
                {errors.locationAddress.message}
              </p>
            )}

            <div className="job-form-input">
              {/* keep same label/id for styling/analytics */}
              <label htmlFor="locationAddress">Location Address</label>

              {isLoaded ? (
                <Autocomplete
                  onLoad={setAuto}
                  onPlaceChanged={handlePlaceChanged}
                  options={{
                    fields: ["address_components"],
                    types: ["address"],
                    componentRestrictions: { country: ["us"] },
                  }}
                >
                  {/* Visible search box (NOT registered with RHF) */}
                  <input
                    id="locationAddress" // same id to keep styling/QA hooks
                    name={ghostName} // random name to dodge autofill heuristics
                    type="text"
                    className="input"
                    value={addrSearch}
                    onChange={(e) => {
                      const val = e.target.value;
                      setAddrSearch(val);
                      setValue("locationAddress", val, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                    autoComplete="new-password" // stronger than "off" for Chrome
                    inputMode="search"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    form="__noform" // detach from real form submission
                    readOnly // guard ON by default
                    onPointerDown={(e) => {
                      const el = e.currentTarget;
                      if (el.readOnly) {
                        e.preventDefault(); // block Chrome’s autofill popup
                        el.focus();
                        requestAnimationFrame(() => {
                          el.readOnly = false;
                        });
                      }
                    }}
                    onFocus={(e) => {
                      const el = e.currentTarget;
                      if (el.readOnly) {
                        requestAnimationFrame(() => {
                          el.readOnly = false;
                        });
                      }
                    }}
                    onBlur={(e) => {
                      e.currentTarget.readOnly = true;
                    }} // re-arm guard
                    onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                    data-lpignore="true" // ask password managers to ignore
                    data-1p-ignore="true"
                    ref={locationAddrRef}
                  />
                </Autocomplete>
              ) : (
                // Fallback while Maps script loads
                <input
                  id="locationAddress"
                  name={ghostName}
                  type="text"
                  className="input"
                  value={addrSearch}
                  onChange={(e) => {
                    const val = e.target.value;
                    setAddrSearch(val);
                    setValue("locationAddress", val, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  autoComplete="new-password"
                  inputMode="search"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  form="__noform"
                  readOnly
                  onPointerDown={(e) => {
                    const el = e.currentTarget;
                    if (el.readOnly) {
                      e.preventDefault();
                      el.focus();
                      requestAnimationFrame(() => {
                        el.readOnly = false;
                      });
                    }
                  }}
                  onFocus={(e) => {
                    const el = e.currentTarget;
                    if (el.readOnly) {
                      requestAnimationFrame(() => {
                        el.readOnly = false;
                      });
                    }
                  }}
                  onBlur={(e) => {
                    e.currentTarget.readOnly = true;
                  }}
                  onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                  ref={locationAddrRef}
                />
              )}

              {/* The real RHF field (hidden) that stores just "123 Main St" */}
              <input type="hidden" {...register("locationAddress")} />
            </div>
          </div>

          <div className="city-state-zip">
            <div
              className={
                errors.city
                  ? "job-form-input-container error"
                  : "job-form-input-container"
              }
            >
              {errors.city && (
                <p className="error-message">
                  <span style={{ marginRight: "5px" }}>
                    <FaExclamationCircle />
                  </span>
                  {errors.city.message}
                </p>
              )}
              <div className="job-form-input">
                <label htmlFor="city">City</label>
                <input
                  id="city"
                  type="text"
                  cols={35}
                  placeholder=""
                  className="input"
                  {...register("city")}
                />
              </div>
            </div>

            <div
              className={
                errors.state
                  ? "job-form-input-container error"
                  : "job-form-input-container"
              }
            >
              {errors.state && (
                <p className="error-message">
                  <span style={{ marginRight: "5px" }}>
                    <FaExclamationCircle />
                  </span>
                  {errors.state.message}
                </p>
              )}
              <div className="job-form-input">
                <label htmlFor="state">State</label>
                <Controller
                  name="state"
                  control={control}
                  defaultValue={null}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <Select
                      id="state"
                      className="select-input"
                      placeholder="Select state"
                      options={stateOptions}
                      onChange={(selectedOption) => onChange(selectedOption)}
                      isClearable
                      value={value}
                      onBlur={onBlur}
                    />
                  )}
                />
              </div>
            </div>

            <div
              className={
                errors.zipcode
                  ? "job-form-input-container error"
                  : "job-form-input-container"
              }
            >
              {errors.zipcode && (
                <p className="error-message">
                  <span style={{ marginRight: "5px" }}>
                    <FaExclamationCircle />
                  </span>
                  {errors.zipcode.message}
                </p>
              )}
              <div className="job-form-input">
                <label htmlFor="zipcode">Zip</label>
                <input
                  id="zipcode"
                  type="text"
                  cols={35}
                  placeholder=""
                  className="input"
                  {...register("zipcode")}
                />
              </div>
            </div>

            {/* <Input
                {...stateValidation}
                options={stateOptions}
                control={control}
              /> */}
          </div>
          <div
            className={
              errors.sqftLeased
                ? "job-form-input-container error"
                : "job-form-input-container"
            }
          >
            {errors.sqftLeased && (
              <p className="error-message">
                <span style={{ marginRight: "5px" }}>
                  <FaExclamationCircle />
                </span>
                {errors.sqftLeased.message}
              </p>
            )}
            <div className="job-form-input sqft">
              <label htmlFor="sqftLeased">
                Square Feet Leased
                <ToolTip text="The amount of space dedicated to the Lessee's employees and should never include building common areas such as hallways, lobbies, and restrooms, or space shared with the lessor or other tenants such as conference rooms, kitchen/break areas, and reception areas.">
                  <IoMdInformationCircleOutline />
                </ToolTip>
              </label>
              <input
                id="sqftLeased"
                type="number"
                min={50}
                placeholder=""
                {...register("sqftLeased")}
                onBlur={(e) => {
                  handleInputBlur(e);
                  // allow re-ask on next change
                  clearOverride("sqftLeased");
                  askedRef.current.delete("sqftLeased");
                  // evaluate blur-only trigger
                  checkSqftAverageOnChange();
                }}
              />
            </div>
          </div>

          <div className="job-form-input-container">
            <div className="job-form-input">
              <label htmlFor="description">Additional Description</label>
              <textarea
                id="description"
                rows={2}
                placeholder=""
                className="input"
                cols={35}
                {...register("description")}
              />
            </div>
          </div>

          <div className="startdate-term">
            <div
              className={
                errors.startDate
                  ? "job-form-input-container error"
                  : "job-form-input-container"
              }
            >
              {errors.startDate && (
                <p className="error-message">
                  <span style={{ marginRight: "5px" }}>
                    <FaExclamationCircle />
                  </span>
                  {errors.startDate.message}
                </p>
              )}
              <div className="job-form-input">
                <label htmlFor="startDate">Expected Start Date</label>
                <input
                  id="startDate"
                  type="date"
                  className="input"
                  placeholder="mm/dd/yyyy"
                  min={MIN_START}
                  max={MAX_START}
                  {...register("startDate", { valueAsDate: true })}
                />
              </div>
            </div>

            <div
              className={
                errors.term
                  ? "job-form-input-container error"
                  : "job-form-input-container"
              }
            >
              {errors.term && (
                <p className="error-message">
                  <span style={{ marginRight: "5px" }}>
                    <FaExclamationCircle />
                  </span>
                  {errors.term.message}
                </p>
              )}
              <div className="job-form-input">
                <label style={{ width: "170px" }} htmlFor="term">
                  Expected Term
                </label>
                <Controller
                  name="term"
                  control={control}
                  defaultValue={null}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Select
                      id="term"
                      className="select-input"
                      placeholder="Select Term"
                      options={termOptions}
                      onChange={(selectedOption) => onChange(selectedOption)}
                      value={value}
                      onBlur={onBlur}
                      isClearable
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div
            className={
              errors.desks
                ? "job-form-input-container error"
                : "job-form-input-container"
            }
          >
            {errors.desks && (
              <p className="error-message">
                <span style={{ marginRight: "5px" }}>
                  <FaExclamationCircle />
                </span>
                {errors.desks.message}
              </p>
            )}
            <div className="job-form-input">
              <label htmlFor="desks">
                Number of Desks/Workstations in the Leased Space
                <ToolTip text="The number of workstations or desks that the Lessee's employees will occupy and is used to estimate the value of amenities such as furniture, internet access, and phone lines, when these amenities are included in the lease.">
                  <IoMdInformationCircleOutline />
                </ToolTip>
              </label>
              <input
                id="desks"
                type="number"
                min={1}
                placeholder=""
                {...register("desks")}
                onBlur={(e) => {
                  handleInputBlur(e);
                  // allow re-ask on next change
                  clearOverride("sqftLeased");
                  askedRef.current.delete("sqftLeased");
                  // evaluate blur-only trigger
                  checkSqftAverageOnChange();
                }}
              />
            </div>
          </div>

          <div className="check-header-container">
            <p className="checkbox-header">{`Space Descriptions: `}</p>
            <span className="space-extra">
              {`[provide any photos, floorplans, or other information as
                necessary]`}
            </span>
          </div>
          <div className="checkbox-outer-container">
            <div
              className={
                errors.propertyType
                  ? "property-type-container error"
                  : "property-type-container"
              }
            >
              {errors.propertyType && (
                <p className="error-message">
                  {" "}
                  <span style={{ marginRight: "5px" }}>
                    <FaExclamationCircle />
                  </span>
                  {errors.propertyType.message}
                </p>
              )}
              <p className="checkbox-heading">Property Type</p>
              <Controller
                name="propertyType"
                control={control}
                defaultValue="" // Default value can be empty or a specific value
                render={({ field }) => (
                  <div>
                    <label className="radio-container" htmlFor="prop-office">
                      Office
                      <div className="job-form-input-radio-check">
                        <input
                          id="prop-office"
                          type="radio"
                          value="Office"
                          checked={field.value === "Office"}
                          onChange={() => field.onChange("Office")}
                        />
                        <span className="checkmark"></span>
                      </div>
                    </label>
                    <label
                      className="radio-container"
                      htmlFor="prop-industrial"
                    >
                      Industrial/Warehouse
                      <div className="job-form-input-radio-check">
                        <input
                          id="prop-industrial"
                          type="radio"
                          value="Industrial/Warehouse"
                          checked={field.value === "Industrial/Warehouse"}
                          onChange={() =>
                            field.onChange("Industrial/Warehouse")
                          }
                        />
                        <span className="checkmark"></span>
                      </div>
                    </label>
                    <label className="radio-container" htmlFor="prop-retail">
                      Retail
                      <div className="job-form-input-radio-check">
                        <input
                          id="prop-retail"
                          type="radio"
                          value="Retail"
                          checked={field.value === "Retail"}
                          onChange={() => field.onChange("Retail")}
                        />
                        <span className="checkmark"></span>
                      </div>
                    </label>
                    <label className="radio-container" htmlFor="prop-mix">
                      Mixed/Other
                      <div className="job-form-input-radio-check">
                        <input
                          id="prop-mix"
                          type="radio"
                          value="Mixed/Other"
                          checked={field.value === "Mixed/Other"}
                          onChange={() => field.onChange("Mixed/Other")}
                        />
                        <span className="checkmark"></span>
                      </div>
                    </label>
                  </div>
                )}
              />
            </div>
            <div
              className={
                errors.spaceType
                  ? "space-type-container error"
                  : "space-type-container"
              }
            >
              {errors.spaceType && (
                <p className="error-message">
                  <span style={{ marginRight: "5px" }}>
                    <FaExclamationCircle />
                  </span>
                  {errors.spaceType.message}
                </p>
              )}
              <p className="checkbox-heading">Space Type</p>
              <Controller
                name="spaceType"
                control={control}
                defaultValue="" // Default value can be empty or a specific value
                render={({ field }) => (
                  <div>
                    <label className="radio-container" htmlFor="space-private">
                      Private office(s)
                      <div className="job-form-input-radio-check">
                        <input
                          id="space-private"
                          type="radio"
                          value="Private office"
                          checked={field.value === "Private office"}
                          onChange={() => field.onChange("Private office")}
                        />
                        <span className="checkmark"></span>
                      </div>
                    </label>
                    <label className="radio-container" htmlFor="space-cube">
                      Cubicle(s)/Other
                      <div className="job-form-input-radio-check">
                        <input
                          id="space-cube"
                          type="radio"
                          value="Cubicle"
                          checked={field.value === "Cubicle"}
                          onChange={() => field.onChange("Cubicle")}
                        />
                        <span className="checkmark"></span>
                      </div>
                    </label>
                  </div>
                )}
              />
            </div>
            <div
              className={
                errors.settingType
                  ? "setting-type-container error"
                  : "setting-type-container"
              }
            >
              {errors.settingType && (
                <p className="error-message">
                  <span style={{ marginRight: "5px" }}>
                    <FaExclamationCircle />
                  </span>
                  {errors.settingType.message}
                </p>
              )}
              <p className="checkbox-heading">
                Setting
                <ToolTip
                  direction="right"
                  text={`Can be Interior or Exterior/Window. Exterior/Window should only be checked when the office is located on the perimeter of the building and has a window with an outside view. If the office is located on the interior of the building and has a window, then select "Interior". If "Interior" and "Exterior/Window" are checked, we will value the space as an interior office. If there are multiple offices being subleased, and they have different settings, please elaborate in the "Additional Description".`}
                >
                  <IoMdInformationCircleOutline />
                </ToolTip>
              </p>

              <Controller
                name="settingType"
                control={control}
                defaultValue="" // Default value can be empty or a specific value
                render={({ field }) => (
                  <div>
                    <label
                      className="radio-container"
                      htmlFor="setting-interior"
                    >
                      Interior
                      <div className="job-form-input-radio-check">
                        <input
                          id="setting-interior"
                          type="radio"
                          value="Interior"
                          checked={field.value === "Interior"}
                          onChange={() => field.onChange("Interior")}
                        />
                        <span className="checkmark"></span>
                      </div>
                    </label>
                    <label className="radio-container" htmlFor="setting-window">
                      Exterior/Window
                      <div className="job-form-input-radio-check">
                        <input
                          id="setting-window"
                          type="radio"
                          value="Window"
                          checked={field.value === "Window"}
                          onChange={() => field.onChange("Window")}
                        />
                        <span className="checkmark"></span>
                      </div>
                    </label>
                  </div>
                )}
              />
            </div>
          </div>
          <div className="check-header-container">
            <p className="checkbox-header">{`Services Provided in Lease: `}</p>
          </div>
          <div className="checkbox-outer-container">
            <div className="property-type-container">
              <p className="checkbox-heading">Facilities</p>
              <Controller
                name="facility"
                control={control}
                defaultValue={[]} // Default value as an empty array
                render={({ field }) => {
                  const ALL = [
                    "cam",
                    "furnished",
                    "janitorial",
                    "parking",
                    "tax",
                  ];
                  const allSelected = ALL.every((v) => field.value.includes(v));
                  const noneSelected = ALL.every(
                    (v) => !field.value.includes(v)
                  );
                  const someSelected = !allSelected && !noneSelected;

                  return (
                    <>
                      <div className="select-all">
                        <label
                          className="radio-container"
                          htmlFor="fac-select-all"
                        >
                          Select all
                          <div className="job-form-input-radio-check">
                            <input
                              id="fac-select-all"
                              type="checkbox"
                              checked={allSelected}
                              ref={(el) => {
                                if (el) el.indeterminate = someSelected;
                              }}
                              onChange={() => {
                                field.onChange(allSelected ? [] : ALL);
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>
                      </div>
                      <div>
                        <label className="radio-container" htmlFor="fac-cam">
                          Common area maintenance
                          <div className="job-form-input-radio-check">
                            <input
                              id="fac-cam"
                              type="checkbox"
                              value="cam"
                              checked={field.value.includes("cam")}
                              onChange={() => {
                                const newValue = field.value.includes("cam")
                                  ? field.value.filter((v) => v !== "cam")
                                  : [...field.value, "cam"];
                                field.onChange(newValue);
                                clearOverride("cam");
                              }}
                            />

                            <span className="checkmark"></span>
                          </div>
                          <ToolTip
                            text={`Usually included in a desk rental or office lease. Unless the Lessee will be paying for CAM in addition to (outside of) the lease amount, this box should be checked as included.`}
                          >
                            <IoMdInformationCircleOutline />
                          </ToolTip>
                        </label>
                        <label
                          className="radio-container"
                          htmlFor="fac-furnished"
                        >
                          Furniture and furnishings
                          <div className="job-form-input-radio-check">
                            <input
                              id="fac-furnished"
                              type="checkbox"
                              value="furnished"
                              checked={field.value.includes("furnished")}
                              onChange={() => {
                                const newValue = field.value.includes(
                                  "furnished"
                                )
                                  ? field.value.filter((v) => v !== "furnished")
                                  : [...field.value, "furnished"];
                                field.onChange(newValue);
                              }}
                              onFocus={() => {
                                if (overrideState["furnished"]) {
                                  setOverrideState((prev) => ({
                                    ...prev,
                                    furnished: false,
                                  }));
                                }
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>
                        <label
                          className="radio-container"
                          htmlFor="fac-janitorial"
                        >
                          Janitorial
                          <div className="job-form-input-radio-check">
                            <input
                              id="fac-janitorial"
                              type="checkbox"
                              value="janitorial"
                              checked={field.value.includes("janitorial")}
                              onChange={() => {
                                const newValue = field.value.includes(
                                  "janitorial"
                                )
                                  ? field.value.filter(
                                      (v) => v !== "janitorial"
                                    )
                                  : [...field.value, "janitorial"];
                                field.onChange(newValue);
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>
                        <label
                          className="radio-container"
                          htmlFor="fac-parking"
                        >
                          Parking
                          <div className="job-form-input-radio-check">
                            <input
                              id="fac-parking"
                              type="checkbox"
                              value="parking"
                              checked={field.value.includes("parking")}
                              onChange={() => {
                                const newValue = field.value.includes("parking")
                                  ? field.value.filter((v) => v !== "parking")
                                  : [...field.value, "parking"];
                                field.onChange(newValue);
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>
                        <label className="radio-container" htmlFor="fac-tax">
                          Property insurance and taxes
                          <div className="job-form-input-radio-check">
                            <input
                              id="fac-tax"
                              type="checkbox"
                              value="tax"
                              checked={field.value.includes("tax")}
                              onChange={() => {
                                const newValue = field.value.includes("tax")
                                  ? field.value.filter((v) => v !== "tax")
                                  : [...field.value, "tax"];
                                field.onChange(newValue);
                                clearOverride("tax");
                              }}
                            />

                            <span className="checkmark"></span>
                          </div>
                          <ToolTip
                            text={`Usually included in a desk rental or office lease. Unless the Lessee will be paying for Property Taxes and Property Insurance in addition to (outside of) the lease amount, this box should be checked as included.`}
                          >
                            <IoMdInformationCircleOutline />
                          </ToolTip>
                        </label>
                      </div>
                    </>
                  );
                }}
              />
            </div>
            <div className="space-type-container">
              <p className="checkbox-heading">Utilities</p>
              <Controller
                name="utility"
                control={control}
                defaultValue={[]} // Default value as an empty array
                render={({ field }) => {
                  const ALL = [
                    "electricity",
                    "gas",
                    "water",
                    "phone",
                    "internet",
                  ];
                  const allSelected = ALL.every((v) => field.value.includes(v));
                  const noneSelected = ALL.every(
                    (v) => !field.value.includes(v)
                  );
                  const someSelected = !allSelected && !noneSelected;
                  return (
                    <>
                      <div className="select-all">
                        <label
                          className="radio-container"
                          htmlFor="util-select-all"
                        >
                          Select all
                          <div className="job-form-input-radio-check">
                            <input
                              id="util-select-all"
                              type="checkbox"
                              checked={allSelected}
                              ref={(el) => {
                                if (el) el.indeterminate = someSelected;
                              }}
                              onChange={() => {
                                field.onChange(allSelected ? [] : ALL);
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>
                      </div>
                      <div>
                        <label
                          className="radio-container"
                          htmlFor="util-electricity"
                        >
                          Electricity
                          <div className="job-form-input-radio-check">
                            <input
                              id="util-electricity"
                              type="checkbox"
                              value="electricity"
                              checked={field.value.includes("electricity")}
                              onChange={() => {
                                const newValue = field.value.includes(
                                  "electricity"
                                )
                                  ? field.value.filter(
                                      (v) => v !== "electricity"
                                    )
                                  : [...field.value, "electricity"];
                                field.onChange(newValue);
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>

                        <label className="radio-container" htmlFor="util-gas">
                          Gas/Oil/Propane
                          <div className="job-form-input-radio-check">
                            <input
                              id="util-gas"
                              type="checkbox"
                              value="gas"
                              checked={field.value.includes("gas")}
                              onChange={() => {
                                const newValue = field.value.includes("gas")
                                  ? field.value.filter((v) => v !== "gas")
                                  : [...field.value, "gas"];
                                field.onChange(newValue);
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>

                        <label className="radio-container" htmlFor="util-water">
                          Water
                          <div className="job-form-input-radio-check">
                            <input
                              id="util-water"
                              type="checkbox"
                              value="water"
                              checked={field.value.includes("water")}
                              onChange={() => {
                                const newValue = field.value.includes("water")
                                  ? field.value.filter((v) => v !== "water")
                                  : [...field.value, "water"];
                                field.onChange(newValue);
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>

                        <label className="radio-container" htmlFor="util-phone">
                          Phone
                          <div className="job-form-input-radio-check">
                            <input
                              id="util-phone"
                              type="checkbox"
                              value="phone"
                              checked={field.value.includes("phone")}
                              onChange={() => {
                                const newValue = field.value.includes("phone")
                                  ? field.value.filter((v) => v !== "phone")
                                  : [...field.value, "phone"];
                                field.onChange(newValue);
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>

                        <label
                          className="radio-container"
                          htmlFor="util-internet"
                        >
                          Internet/WiFi
                          <div className="job-form-input-radio-check">
                            <input
                              id="util-internet"
                              type="checkbox"
                              value="internet"
                              checked={field.value.includes("internet")}
                              onChange={() => {
                                const newValue = field.value.includes(
                                  "internet"
                                )
                                  ? field.value.filter((v) => v !== "internet")
                                  : [...field.value, "internet"];
                                field.onChange(newValue);
                              }}
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>
                      </div>
                    </>
                  );
                }}
              />
            </div>
            <div className="setting-type-container">
              <p className="checkbox-heading">Amenities</p>
              <Controller
                name="amenity"
                control={control}
                defaultValue={[]} // Default value as an empty array
                render={({ field }) => {
                  const ALL = ["meeting", "kitchen", "copy", "receptionist"];
                  const allSelected = ALL.every((v) => field.value.includes(v));
                  const noneSelected = ALL.every(
                    (v) => !field.value.includes(v)
                  );
                  const someSelected = !allSelected && !noneSelected;
                  return (
                    <>
                      <div className="select-all">
                        <label
                          className="radio-container"
                          htmlFor="amen-select-all"
                        >
                          Select all
                          <div className="job-form-input-radio-check">
                            <input
                              id="amen-select-all"
                              type="checkbox"
                              checked={allSelected}
                              ref={(el) => {
                                if (el) el.indeterminate = someSelected;
                              }}
                              onChange={() =>
                                field.onChange(allSelected ? [] : ALL)
                              }
                            />
                            <span className="checkmark"></span>
                          </div>
                        </label>
                      </div>
                      <div>
                        <label
                          className="radio-container"
                          htmlFor="amen-meeting"
                        >
                          Furnished conference room use
                          <div className="job-form-input-radio-check">
                            <input
                              id="amen-meeting"
                              type="checkbox"
                              value="meeting"
                              checked={field.value.includes("meeting")}
                              onChange={() => {
                                const newValue = field.value.includes("meeting")
                                  ? field.value.filter((v) => v !== "meeting")
                                  : [...field.value, "meeting"];
                                field.onChange(newValue);
                                clearOverride("meeting");
                              }}
                            />

                            <span className="checkmark"></span>
                          </div>
                        </label>

                        <label
                          className="radio-container"
                          htmlFor="amen-kitchen"
                        >
                          Kitchen
                          <div className="job-form-input-radio-check">
                            <input
                              id="amen-kitchen"
                              type="checkbox"
                              value="kitchen"
                              checked={field.value.includes("kitchen")}
                              onChange={() => {
                                const newValue = field.value.includes("kitchen")
                                  ? field.value.filter((v) => v !== "kitchen")
                                  : [...field.value, "kitchen"];
                                field.onChange(newValue);
                                clearOverride("kitchen");
                              }}
                            />

                            <span className="checkmark"></span>
                          </div>
                        </label>

                        <label className="radio-container" htmlFor="amen-copy">
                          Copy/fax/printer/scanner use
                          <div className="job-form-input-radio-check">
                            <input
                              id="amen-copy"
                              type="checkbox"
                              value="copy"
                              checked={field.value.includes("copy")}
                              onChange={() => {
                                const newValue = field.value.includes("copy")
                                  ? field.value.filter((v) => v !== "copy")
                                  : [...field.value, "copy"];
                                field.onChange(newValue);
                                clearOverride("copy");
                              }}
                            />

                            <span className="checkmark"></span>
                          </div>
                        </label>

                        <label
                          className="radio-container"
                          htmlFor="amen-receptionist"
                        >
                          Receptionist/Answering
                          <div className="job-form-input-radio-check">
                            <input
                              id="amen-receptionist"
                              type="checkbox"
                              value="receptionist"
                              checked={field.value.includes("receptionist")}
                              onChange={() => {
                                const newValue = field.value.includes(
                                  "receptionist"
                                )
                                  ? field.value.filter(
                                      (v) => v !== "receptionist"
                                    )
                                  : [...field.value, "receptionist"];
                                field.onChange(newValue);
                                clearOverride("receptionist");
                              }}
                            />

                            <span className="checkmark"></span>
                          </div>
                        </label>
                      </div>
                    </>
                  );
                }}
              />
            </div>
          </div>
          <div className="checkbox-outer-container">
            <div className="property-type-container">
              <p className="checkbox-header upload-header">{`Upload images: `}</p>
            </div>
          </div>
          <input type="file" onChange={handleChange} multiple />
          <div className="button-container">
            {!isPartner && (
              <button
                type="button"
                className="submit-btn exhibit-save"
                onClick={async () => {
                  // Navigate back without touching the global loader.
                  const goToDashboard = () =>
                    navigate("/dashboard", {
                      replace: true,
                      state: { fromSave: true },
                    });

                  if (!isDirty) {
                    goToDashboard();
                    return;
                  }

                  const data = getValues();
                  await onSubmit(data, "Saved");

                  if (
                    !errorOpen ||
                    (Array.isArray(errorOpen) && errorOpen.length === 0)
                  ) {
                    goToDashboard();
                  }
                }}
                title={isDirty ? "Save & Exit" : "Back"}
                aria-label={isDirty ? "Save & Exit" : "Back"}
              >
                {isDirty ? "Save & Exit" : "Back"}
              </button>
            )}
            <div
              className={Object.keys(errors).length > 0 ? "error" : ""}
              style={{ display: "flex", flexDirection: "column" }}
            >
              {Object.keys(errors).length > 0 && (
                <p className="error-message submit-error">
                  {" "}
                  <span style={{ marginRight: "5px" }}>
                    <FaExclamationCircle />
                  </span>
                  Please fix the errors above before continuing.
                </p>
              )}
              <button
                className="submit-btn exhibit-submit"
                type="button"
                onClick={handleSubmit(async (data) => {
                  dispatch(setLoading(true));
                  try {
                    await onSubmit(data, "In Progress"); // stays on this page
                  } finally {
                    // Always release the global spinner so the page doesn't get "hung"
                    dispatch(setLoading(false));
                  }
                })}
              >
                {!isPartner ? "Save & Continue" : "Submit"}
              </button>{" "}
            </div>
          </div>

          {isPartner && (
            <p className="partner-submit">
              {`Once submitted, `}
              <span style={{ fontStyle: "italic" }}>
                {unSlugify(extractSlugPart(location.pathname))}
              </span>
              {` will be notified via email. You can close this window to cancel or refresh to start over.`}
            </p>
          )}
        </form>
      </div>

      {isDirty && (
        <div className="clear-btn-wrapper">
          <button
            type="button"
            className="clear-btn"
            onClick={() => setShowClearConfirm(true)}
          >
            Clear All
          </button>
        </div>
      )}
      {showClearConfirm && (
        <div
          className="modal-container"
          role="dialog"
          aria-modal="true"
          aria-labelledby="clear-confirm-title"
        >
          <div className="my-modal">
            <h2 id="clear-confirm-title" className="modal-title">
              Clear all fields?
            </h2>

            <p className="modal-text">
              This will reset every field in the form.
            </p>

            <p className="modal-subtext">This action cannot be undone.</p>

            <div className="modal-btn-cont">
              <button
                type="button"
                className="modal-cancel"
                onClick={() => setShowClearConfirm(false)}
              >
                Cancel
              </button>

              <button
                type="button"
                className="modal-delete"
                onClick={() => {
                  setShowClearConfirm(false);
                  handleClearAll();
                }}
              >
                Clear all
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Form;
